import React, { useEffect, useRef, useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { CheckCircle2, ArrowRight, Copy, Loader2, XCircle } from "lucide-react";
import { toast } from "sonner";
import { api, setClientToken } from "../lib/api";

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("en-AU", {
      day: "numeric", month: "short", year: "numeric",
    });
  } catch { return iso; }
}

const MAX_ATTEMPTS = 12;
const INTERVAL_MS = 2000;

export default function SignupSuccess() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("checking"); // checking | active | pending | expired | error
  const [details, setDetails] = useState(null);
  const attemptsRef = useRef(0);

  useEffect(() => {
    if (!sessionId) {
      setState("error");
      return;
    }
    let cancelled = false;
    const poll = async () => {
      if (cancelled) return;
      attemptsRef.current += 1;
      try {
        const { data } = await api.get(`/checkout/status/${sessionId}`);
        setDetails(data);
        if (data.status === "complete" && data.client_status && data.client_status !== "pending") {
          if (data.token) setClientToken(data.token);
          setState("active");
          return;
        }
        if (data.status === "expired") {
          setState("expired");
          return;
        }
        if (attemptsRef.current >= MAX_ATTEMPTS) {
          setState("pending");
          return;
        }
        setTimeout(poll, INTERVAL_MS);
      } catch (e) {
        if (attemptsRef.current >= MAX_ATTEMPTS) setState("error");
        else setTimeout(poll, INTERVAL_MS);
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId]);

  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const embedSnippet = details?.widget_id
    ? `<iframe src="${origin}/widget/${details.widget_id}" width="100%" height="640" frameborder="0"></iframe>`
    : "";
  const copy = (text, label) => {
    navigator.clipboard?.writeText(text);
    toast.success(`${label} copied`);
  };

  return (
    <div className="relative min-h-[80vh] bg-[#0A1628] text-[#F8FAFC] py-24">
      <div className="absolute inset-0 so-grid opacity-25 pointer-events-none" />
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[600px] so-glow-cyan pointer-events-none" />

      <div className="relative max-w-2xl mx-auto px-6">
        <div className="so-card rounded-2xl p-10 text-center" data-testid="signup-success-card">
          {state === "checking" && (
            <>
              <Loader2 className="mx-auto text-[#06B6D4] animate-spin" size={36} />
              <h1 className="mt-6 font-display text-2xl font-bold tracking-tight">
                Activating your trial…
              </h1>
              <p className="mt-3 text-[#94A3B8] text-sm">
                Confirming with Stripe. This usually takes a few seconds.
              </p>
            </>
          )}

          {state === "active" && details && (
            <>
              <CheckCircle2 className="mx-auto text-[#06B6D4]" size={42} />
              <h1 className="mt-6 font-display text-3xl font-bold tracking-tight">
                Your free trial has started.
              </h1>
              <p className="mt-3 text-[#94A3B8] text-sm">
                {details.business_name ? <><span className="text-[#F8FAFC]">{details.business_name}</span> is live on a 28-day free trial. </> : ""}
                We'll bill you <span className="text-[#F8FAFC] font-medium">$79 AUD</span> on{" "}
                <span className="text-[#F8FAFC] font-medium">{fmtDate(details.trial_end_date)}</span> — cancel anytime before then.
              </p>

              <div className="mt-8 text-left">
                <div className="uppercase text-[11px] tracking-widest text-[#94A3B8] font-mono">Your widget ID</div>
                <div className="mt-2 flex items-center justify-between gap-3 px-4 py-3 rounded-lg bg-[#091324] border border-white/10" data-testid="widget-id-display">
                  <code className="text-[#06B6D4] text-sm font-mono break-all">{details.widget_id}</code>
                  <button onClick={() => copy(details.widget_id, "Widget ID")} className="text-[#94A3B8] hover:text-[#F8FAFC] shrink-0" aria-label="Copy" data-testid="copy-widget-id"><Copy size={14} /></button>
                </div>

                <div className="mt-5 uppercase text-[11px] tracking-widest text-[#94A3B8] font-mono">Embed snippet</div>
                <div className="mt-2 flex items-start justify-between gap-3 px-4 py-3 rounded-lg bg-[#091324] border border-white/10" data-testid="embed-snippet-display">
                  <code className="text-[#F8FAFC]/85 text-xs font-mono break-all leading-relaxed">{embedSnippet}</code>
                  <button onClick={() => copy(embedSnippet, "Embed snippet")} className="text-[#94A3B8] hover:text-[#F8FAFC] shrink-0" aria-label="Copy" data-testid="copy-embed-snippet"><Copy size={14} /></button>
                </div>
              </div>

              <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
                <Link to="/account" data-testid="success-account-btn" className="btn-blue inline-flex items-center justify-center gap-2 rounded-full px-5 py-3 text-sm font-medium">
                  Go to my account
                </Link>
                <Link to="/" data-testid="success-home-btn" className="btn-ghost inline-flex items-center justify-center gap-2 rounded-full px-5 py-3 text-sm">
                  Back to homepage <ArrowRight size={14} />
                </Link>
              </div>
            </>
          )}

          {state === "pending" && (
            <>
              <Loader2 className="mx-auto text-[#06B6D4]" size={36} />
              <h1 className="mt-6 font-display text-2xl font-bold tracking-tight">
                Still confirming…
              </h1>
              <p className="mt-3 text-[#94A3B8] text-sm">
                Stripe is still finalising. We'll email you the moment your account activates — feel free to refresh in a minute.
              </p>
            </>
          )}

          {state === "expired" && (
            <>
              <XCircle className="mx-auto text-[#EC4899]" size={42} />
              <h1 className="mt-6 font-display text-2xl font-bold tracking-tight">
                Checkout session expired
              </h1>
              <Link to="/pricing" className="btn-blue inline-flex items-center gap-2 rounded-full px-5 py-3 text-sm font-medium mt-8">
                Back to pricing
              </Link>
            </>
          )}

          {state === "error" && (
            <>
              <XCircle className="mx-auto text-[#EC4899]" size={42} />
              <h1 className="mt-6 font-display text-2xl font-bold tracking-tight">
                We couldn't verify your checkout
              </h1>
              <p className="mt-3 text-[#94A3B8] text-sm">
                If you were charged, please contact support and we'll sort it immediately.
              </p>
              <Link to="/pricing" className="btn-blue inline-flex items-center gap-2 rounded-full px-5 py-3 text-sm font-medium mt-8">
                Back to pricing
              </Link>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
